import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiPlay, FiClock, FiEye, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import { Video } from '@/data/mockData';
import api from '@/utils/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
    overflow: visible;
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const SearchBarWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.onBackground};
  outline: none;

  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:blur {
    border-color: #cbd5e0;
    box-shadow: none;
  }
`;

const SearchHelper = styled.p`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #718096;
`;

const AccordionSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`;

const AccordionHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.lg};
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  border-bottom: ${({ theme, $isExpanded }) =>
    $isExpanded ? `1px solid ${theme.colors.border}` : 'none'};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.onBackground};
`;

const AccordionContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 2vw, 1rem);
  margin-bottom: clamp(0.75rem, 2vw, 1.5rem);
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.h1`
  font-size: clamp(1.25rem, 3vw, 1.875rem);
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md} 0;
    overflow: visible;
  }

  @media (max-width: 640px) {
    padding: 0;
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: clamp(1rem, 2vw, 2rem);
  margin-bottom: clamp(0.75rem, 2vw, 1.5rem);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const VideoCard = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  flex-direction: column;
  height: clamp(300px, 40vw, 400px);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1;
  transition: ${({ theme }) => theme.transitions.default};
  
  ${VideoCard}:hover & {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  z-index: 2;

  svg {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => theme.colors.primary};
    margin-left: 4px;
  }

  ${VideoCard}:hover & {
    transform: scale(1.1);
    background: ${({ theme }) => theme.colors.primary};

    svg {
      color: white;
    }
  }
`;

const VideoInfo = styled.div`
  padding: clamp(0.75rem, 2vw, 1.5rem);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const VideoTitle = styled.h3`
  font-size: clamp(0.875rem, 2vw, 1.125rem);
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: clamp(0.75rem, 1.5vw, 0.875rem);
  color: ${({ theme }) => theme.colors.muted};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const VideoCount = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: clamp(0.75rem, 1.5vw, 0.875rem);
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme}) => theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0;
  max-width: 400px;
`;

const humanizeCategory = (category: string) => {
  if (!category) return 'Other';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const VideoLibraryPage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Fetch videos from API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await api.get('/videos');
        const vids = response.data.data || [];
        setVideos(vids);
        const categories = new Set<string>(vids.map((video: Video) => video.category || 'other'));
        setExpandedCategories(categories);
      } catch (error: any) {
        console.error('Error fetching videos:', error);
        toast.error(error.response?.data?.message || 'Failed to load videos');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleVideoClick = (video: Video) => {
    navigate(`/video-player/${video._id}`, { state: { video } });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const videosByCategory = videos.reduce((acc, video) => {
    const key = video.category || 'other';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(video);
    return acc;
  }, {} as Record<string, Video[]>);

  const filteredVideosByCategory = Object.entries(videosByCategory).reduce(
    (acc, [category, vids]) => {
      const filtered = vids.filter((video) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
          video.title.toLowerCase().includes(term) ||
          video.description?.toLowerCase().includes(term)
        );
      });
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, Video[]>
  );

  const hasResults = Object.keys(filteredVideosByCategory).length > 0;

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>Video Library</Title>
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Container>
    );
  }

  if (videos.length === 0) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>Video Library</Title>
        </Header>
        <EmptyState>
          <EmptyIcon>🎥</EmptyIcon>
          <EmptyTitle>No videos available</EmptyTitle>
          <EmptyText>There are no published videos at the moment. Check back later!</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Video Library</Title>
        <VideoCount>{videos.length} {videos.length === 1 ? 'Video' : 'Videos'}</VideoCount>
      </Header>

      <Content>
        <SearchBarWrapper>
          <SearchInput
            type="text"
            placeholder="Search videos by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchHelper>{hasResults ? 'Select a category to browse videos.' : 'No videos match your search.'}</SearchHelper>
        </SearchBarWrapper>

        {!hasResults ? (
          <EmptyState>
            <EmptyIcon>🎬</EmptyIcon>
            <EmptyTitle>No videos found</EmptyTitle>
            <EmptyText>Try adjusting your search or clear the filters to see all videos.</EmptyText>
          </EmptyState>
        ) : (
          Object.entries(filteredVideosByCategory).map(([category, categoryVideos]) => (
            <AccordionSection key={category}>
              <AccordionHeader
                $isExpanded={expandedCategories.has(category)}
                onClick={() => toggleCategory(category)}
              >
                <AccordionTitle>
                  {humanizeCategory(category)}
                  <VideoCount>
                    {categoryVideos.length} {categoryVideos.length === 1 ? 'video' : 'videos'}
                  </VideoCount>
                </AccordionTitle>
                {expandedCategories.has(category) ? <FiChevronUp /> : <FiChevronDown />}
              </AccordionHeader>
              {expandedCategories.has(category) && (
                <AccordionContent>
                  <VideoGrid>
                    {categoryVideos.map((video, index) => (
                      <VideoCard
                        key={video._id}
                        onClick={() => handleVideoClick(video)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <VideoThumbnail>
                          {video.thumbnail ? (
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : null}
                          <ThumbnailVideo
                            src={video.url}
                            preload="metadata"
                            muted
                            style={{ display: video.thumbnail ? 'none' : 'block' }}
                          />
                          <ThumbnailOverlay />
                          <PlayButton>
                            <FiPlay />
                          </PlayButton>
                        </VideoThumbnail>
                        <VideoInfo>
                          <VideoTitle>{video.title}</VideoTitle>
                          <VideoMeta>
                            <MetaItem>
                              <FiClock />
                              {video.duration}
                            </MetaItem>
                            {video.views !== undefined && (
                              <MetaItem>
                                <FiEye />
                                {video.views} {video.views === 1 ? 'view' : 'views'}
                              </MetaItem>
                            )}
                          </VideoMeta>
                        </VideoInfo>
                      </VideoCard>
                    ))}
                  </VideoGrid>
                </AccordionContent>
              )}
            </AccordionSection>
          ))
        )}
      </Content>
    </Container>
  );
};
